import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { isNil } from 'lodash';
import { Observable, switchMap } from 'rxjs';
import { BaseDatatableStore, IBaseDatatableState } from '../../../../../state/base-datatable-store.service';
import { ELoadStatus } from '../../../../../state/state.interface';
import { DateUtilitiesService } from '../../../../../utilities/date-utilities.service';
import {
  HttpUtilitiesService,
  IBaseCrudResponse,
  IBaseResponse,
  IGenericCrudRequestConstructionParameters,
  IGetManyResponse,
} from '../../../../../utilities/http-utilities.service';
import {
  IWorkInstructionTemplate,
  TAddWorkInstructionTemplate,
} from '../work-instruction-templates/work-instruction-templates.interface';
import {
  IEditWorkInstructionStep,
  IWorkInstructionStep,
  TAddWorkInstructionStep,
} from './work-instruction-steps.interface';

@Injectable()
export class WorkInstructionStepsStore extends BaseDatatableStore<
  IWorkInstructionStep,
  IBaseDatatableState<IWorkInstructionStep>
> {
  readonly createOne = this.effect((trigger$: Observable<TAddWorkInstructionStep>) =>
    trigger$.pipe(
      switchMap((item: TAddWorkInstructionStep) => {
        this.patchState({ singleCrudLoading: ELoadStatus.loading });

        return this.createWorkInstructionStep(item).pipe(
          tapResponse(
            (): void => {
              this.patchState({ singleCrudLoading: ELoadStatus.success });
            },
            // eslint-disable-next-line no-console,sonarjs/no-duplicate-string
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );
  readonly updateOne = this.effect((trigger$: Observable<IEditWorkInstructionStep>) =>
    trigger$.pipe(
      switchMap((item: IEditWorkInstructionStep) => {
        this.patchState({ singleCrudLoading: ELoadStatus.loading });

        return this.updateWorkInstructionStep(item).pipe(
          tapResponse(
            () => this.patchState({ singleCrudLoading: ELoadStatus.success }),
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  override readonly deleteOne = this.effect((trigger$: Observable<number>) =>
    trigger$.pipe(
      switchMap((id: number) => {
        this.patchState({ singleCrudLoading: ELoadStatus.loading });

        return this.deleteWorkInstructionStep(id).pipe(
          tapResponse(
            () => this.patchState({ singleCrudLoading: ELoadStatus.success }),
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  override readonly deleteBulk = this.effect((trigger$: Observable<number[]>) =>
    trigger$.pipe(
      switchMap((itemIds: number[]) => {
        this.patchState({ bulkCrudLoading: ELoadStatus.loading });

        return this.deleteWorkInstructionSteps(itemIds).pipe(
          tapResponse(
            (response): void => {
              this.patchState({ bulkCrudLoading: ELoadStatus.success, bulkOperationFailedData: response.data });
            },
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  readonly loadWorkInstructionSteps = this.effect((trigger$: Observable<IGenericCrudRequestConstructionParameters>) =>
    trigger$.pipe(
      switchMap((params: IGenericCrudRequestConstructionParameters) => {
        const httpParams: HttpParams = this.httpUtilities.insertGenericCrudRequestParameters(params);
        this.patchState({ dataLoading: ELoadStatus.loading });

        return this.getWorkInstructionSteps(httpParams).pipe(
          tapResponse(
            (response: IGetManyResponse<IWorkInstructionStep>) =>
              this.patchState({
                data: this.formatData(response.data),
                dataLoading: ELoadStatus.success,
                dataTotal: response.total,
              }),
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  constructor(
    private readonly http: HttpClient,
    private readonly httpUtilities: HttpUtilitiesService,
  ) {
    super({
      bulkCrudLoading: ELoadStatus.initial,
      bulkOperationFailedData: [],
      data: [],
      dataLoading: ELoadStatus.initial,
      dataTotal: 0,
      singleCrudLoading: ELoadStatus.initial,
    });
  }

  private createWorkInstructionStep(body: TAddWorkInstructionStep): Observable<IBaseResponse<IWorkInstructionStep>> {
    return this.http.post<IBaseResponse<IWorkInstructionStep>>('guide-steps', body);
  }

  private createWorkInstructionTemplate(
    body: TAddWorkInstructionTemplate,
  ): Observable<IBaseResponse<IWorkInstructionTemplate>> {
    return this.http.post<IBaseResponse<IWorkInstructionTemplate>>('guide-templates', body);
  }

  private updateWorkInstructionStep(
    editGuideStep: IEditWorkInstructionStep,
  ): Observable<IBaseResponse<IWorkInstructionStep>> {
    return this.http.patch<IBaseResponse<IWorkInstructionStep>>(`guide-steps/${editGuideStep.id}`, editGuideStep.dto);
  }

  private deleteWorkInstructionStep(id: number): Observable<IBaseResponse<IWorkInstructionStep>> {
    return this.http.delete<IBaseResponse<IWorkInstructionStep>>(`guide-steps/${id}`);
  }

  private getWorkInstructionSteps(params: HttpParams): Observable<IGetManyResponse<IWorkInstructionStep>> {
    return this.http.get<IGetManyResponse<IWorkInstructionStep>>('guide-steps', { params });
  }

  private deleteWorkInstructionSteps(ids: number[]): Observable<IBaseResponse<IBaseCrudResponse[]>> {
    return this.http.delete<IBaseResponse<IBaseCrudResponse[]>>('guide-steps/bulk/delete', {
      body: { payload: ids },
    });
  }

  private formatData(workInstructionSteps: IWorkInstructionStep[]): IWorkInstructionStep[] {
    return workInstructionSteps.map((workInstructionStep) => {
      return {
        ...workInstructionStep,
        timeToComplete: isNil(workInstructionStep.targetDuration)
          ? null
          : DateUtilitiesService.formatSecondsToTimer(workInstructionStep.targetDuration),
      };
    });
  }
}
